import React, { useState, useEffect, useCallback } from 'react';
import apiClient from '../../../api/apiClient';
import LoadingSpinner from '../../../components/ui/LoadingSpinner';
import ErrorBanner from '../../../components/ui/ErrorBanner';
import { st, colors } from '../shared/meetingStyles';
import Avatar from '../shared/Avatar';
import { BulbIcon, XIcon, CheckCircleIcon, PlayIcon, PauseIcon } from '../shared/Icons';

function fmtTimer(seconds) {
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return `${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
}

function IssueRow({ issue, team, sessionId, onUpdate, onSolve, onDelete, expanded, onToggle }) {
  const [details, setDetails] = useState(issue.details || '');
  const [ownerUserId, setOwnerUserId] = useState(issue.owner_user_id || '');
  const [seconds, setSeconds] = useState(issue.timer_seconds || 0);
  const [running, setRunning] = useState(false);

  useEffect(() => {
    if (!running) return;
    const interval = setInterval(() => setSeconds(s => s + 1), 1000);
    return () => clearInterval(interval);
  }, [running]);

  useEffect(() => { setDetails(issue.details || ''); setOwnerUserId(issue.owner_user_id || ''); setSeconds(issue.timer_seconds || 0); }, [issue]);

  const saveDetails = () => onUpdate(issue.id, { details });
  const saveOwner = (id) => { setOwnerUserId(id); onUpdate(issue.id, { owner_user_id: id || null }); };
  const saveTimer = () => onUpdate(issue.id, { timer_seconds: seconds });

  return (
    <div style={{ borderBottom: '1px solid #f1f5f9', padding: '0.65rem 0.25rem' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '0.6rem' }}>
        <div style={{ flex: 1, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '0.6rem' }} onClick={onToggle}>
          <Avatar name={issue.owner_name} size={22} />
          <span style={{ fontSize: '0.82rem', fontWeight: '600', color: colors.text }}>{issue.title}</span>
        </div>
        {issue.status === 'open' ? (
          <button onClick={() => onSolve(issue.id)} style={{ ...st.primaryBtn, padding: '0.3rem 0.7rem', fontSize: '0.7rem', display: 'inline-flex', alignItems: 'center', gap: '0.3rem' }}><CheckCircleIcon size={13} /> Solve</button>
        ) : (
          <span style={{ fontSize: '0.68rem', color: colors.green, fontWeight: '800', background: colors.greenSoft, padding: '0.2rem 0.6rem', borderRadius: '999px' }}>Solved</span>
        )}
        <button onClick={() => onDelete(issue.id)} style={st.ghostBtn}><XIcon size={13} /></button>
      </div>
      {expanded && (
        <div style={{ marginTop: '0.6rem', marginLeft: '1.75rem', display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
          <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
            <span style={{
              fontFamily: 'monospace', fontSize: '0.85rem', fontWeight: '700', color: colors.text,
              background: colors.bgSubtle, padding: '0.25rem 0.6rem', borderRadius: '0.4rem',
            }}>{fmtTimer(seconds)}</span>
            <button onClick={() => setRunning(r => !r)} style={{ ...st.secondaryBtn, padding: '0.25rem 0.55rem', fontSize: '0.7rem', display: 'inline-flex', alignItems: 'center', gap: '0.3rem' }}>
              {running ? <PauseIcon size={12} /> : <PlayIcon size={12} />}{running ? 'Pause' : 'Start'}
            </button>
            <button onClick={() => { setRunning(false); saveTimer(); }} style={{ ...st.secondaryBtn, padding: '0.25rem 0.55rem', fontSize: '0.7rem' }}>Save time</button>
            <select value={ownerUserId} onChange={e => saveOwner(e.target.value)} style={{ ...st.input, width: '150px', marginLeft: 'auto' }}>
              <option value="">No owner</option>
              {team.map(u => <option key={u.id} value={u.id}>{u.name}</option>)}
            </select>
          </div>
          <textarea value={details} onChange={e => setDetails(e.target.value)} onBlur={saveDetails} placeholder="Details..." style={st.textarea} />
        </div>
      )}
    </div>
  );
}

export default function IssuesSection({ sessionId, meetingId }) {
  const [issues, setIssues] = useState([]);
  const [team, setTeam] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [tab, setTab] = useState('short');
  const [newTitle, setNewTitle] = useState('');
  const [expandedId, setExpandedId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [issuesRes, teamRes] = await Promise.all([
        apiClient.get('/meetings/issues'),
        apiClient.get('/meetings/team'),
      ]);
      setIssues(issuesRes.data.issues || []);
      setTeam(teamRes.data.users || []);
    } catch (err) {
      setError(err.response?.data?.error || err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const addIssue = async (e) => {
    e.preventDefault();
    if (!newTitle.trim()) return;
    await apiClient.post('/meetings/issues', { title: newTitle.trim(), is_long_term: tab === 'long', meeting_id: meetingId });
    setNewTitle('');
    await load();
  };
  const updateIssue = async (id, data) => { await apiClient.put(`/meetings/issues/${id}`, data); await load(); };
  const solveIssue = async (id) => { await apiClient.post(`/meetings/issues/${id}/solve`, { session_id: sessionId }); await load(); };
  const deleteIssue = async (id) => {
    if (!window.confirm('Delete this issue?')) return;
    await apiClient.delete(`/meetings/issues/${id}`); await load();
  };

  if (loading) return <LoadingSpinner />;

  const shortTerm = issues.filter(i => i.status === 'open' && !i.is_long_term);
  const longTerm = issues.filter(i => i.status === 'open' && i.is_long_term);
  const solved = issues.filter(i => i.status === 'solved');
  const list = tab === 'short' ? shortTerm : tab === 'long' ? longTerm : solved;

  return (
    <div style={st.card}>
      {error && <ErrorBanner message={error} />}
      <div style={st.cardHeader}>
        <h2 style={{ ...st.h2, display: 'flex', alignItems: 'center', gap: '0.5rem' }}><BulbIcon size={18} /> Ideas and Challenges</h2>
      </div>
      <div style={{ display: 'flex', gap: '0.3rem', marginBottom: '0.9rem', background: colors.bgSubtle, borderRadius: '0.65rem', padding: '0.3rem' }}>
        <button onClick={() => setTab('short')} style={st.tab(tab === 'short')}>Short-term ({shortTerm.length})</button>
        <button onClick={() => setTab('long')} style={st.tab(tab === 'long')}>Long-term ({longTerm.length})</button>
        <button onClick={() => setTab('solved')} style={st.tab(tab === 'solved')}>Recently Solved ({solved.length})</button>
      </div>
      {tab !== 'solved' && (
        <form onSubmit={addIssue} style={{ display: 'flex', gap: '0.5rem', marginBottom: '0.9rem' }}>
          <input value={newTitle} onChange={e => setNewTitle(e.target.value)} placeholder="Create a quick Issue" style={{ ...st.input, flex: 1 }} />
          <button type="submit" style={st.primaryBtn}>Add</button>
        </form>
      )}
      {list.length === 0 ? (
        <div style={st.emptyState}><BulbIcon size={28} style={{ color: colors.faint }} />Nothing here yet.</div>
      ) : (
        list.map(issue => (
          <IssueRow key={issue.id} issue={issue} team={team} sessionId={sessionId}
            onUpdate={updateIssue} onSolve={solveIssue} onDelete={deleteIssue}
            expanded={expandedId === issue.id} onToggle={() => setExpandedId(expandedId === issue.id ? null : issue.id)} />
        ))
      )}
    </div>
  );
}
